/**
 * Error utilities — API envelope 파싱 + 사용자 친화 메시지.
 *
 * Envelope (BE 측 정의, docs/61_API_RESPONSE_FORMAT.md):
 *   { code: "S2xx" | "E4xx" | "E5xx" | "B1xxx", message: string, data: any }
 */
import type { AxiosError } from "axios";
import i18n from "../i18n";

export interface ApiEnvelope<T = unknown> {
  code: string;
  message: string;
  data: T | null;
}

export interface ValidationViolation {
  field: string;
  message: string;
}

export interface ValidationData {
  errors: ValidationViolation[];
}

export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus?: number;
  readonly data: unknown;

  constructor(code: string, message: string, opts: { httpStatus?: number; data?: unknown } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.httpStatus = opts.httpStatus;
    this.data = opts.data ?? null;
  }

  /** code prefix 가 'B' 인 비즈니스 도메인 오류 */
  get isBusiness(): boolean {
    return this.code.startsWith("B");
  }

  /** code prefix 가 'E' 인 일반 오류 */
  get isError(): boolean {
    return this.code.startsWith("E");
  }
}

/** axios error / Error / unknown 모두에서 사용자 친화 메시지 추출 */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) {
    const ax = err as AxiosError<ApiEnvelope>;
    return ax.response?.data?.message ?? ax.message ?? i18n.t("error.unexpected");
  }
  return i18n.t("error.unexpected");
}

/** envelope 형태인지 검증 (런타임 type guard) */
export function isApiEnvelope(value: unknown): value is ApiEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as ApiEnvelope).code === "string" &&
    typeof (value as ApiEnvelope).message === "string" &&
    "data" in (value as Record<string, unknown>)
  );
}

/** validation 응답 (E400 + data.errors) 인지 */
export function isValidationError(err: unknown): err is ApiError & { data: ValidationData } {
  return (
    err instanceof ApiError &&
    err.code === "E400" &&
    typeof err.data === "object" &&
    err.data !== null &&
    Array.isArray((err.data as ValidationData).errors)
  );
}

/**
 * 사용자가 소셜 로그인 popup/모달을 직접 닫았는지 여부.
 * Provider 별 메시지/코드 패턴이 다르므로 통합 매칭:
 *   - codetrix-studio Google: "The user canceled the sign-in flow." / 12501
 *   - Kakao SDK:              "User canceled login." / "ClientFailed: Cancelled"
 *   - Naver SDK:              "user_cancel" / "사용자가 취소"
 *   - 일반 OAuth popup 닫음:    "popup_closed_by_user" / "access_denied"
 *
 * cancel 은 에러 메시지로 노출하지 않고 silently 무시한다 (사용자 의도된 행동).
 */
export function isUserCancelError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = (err.message ?? "").toLowerCase();
  const code = (err as { code?: string | number }).code;
  if (code === 12501 || code === "12501") return true; // Google Sign-In status code
  if (code === "USER_CANCELLED" || code === "user_cancel") return true;
  return (
    msg.includes("cancel") ||           // "canceled" / "cancelled" 모두 포함
    msg.includes("취소") ||
    msg.includes("popup_closed") ||
    msg.includes("access_denied") ||
    msg.includes("user denied") ||
    // Sign in with Apple: 사용자가 시트를 닫거나(1001) 기기에 Apple 계정 미로그인 상태에서
    // 닫으면(1000 unknown) 실제 로그인 시도가 없으므로 취소로 간주해 조용히 무시한다.
    // 네이티브 미반영 빌드(구 Swift)에서도 동작하도록 메시지로도 잡는다.
    msg.includes("authorizationerror error 1000") ||
    msg.includes("authorizationerror error 1001")
  );
}
